import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { convertBRTtoUTC, convertUTCtoBRT } from '../../utils/timezoneHelpers';
import DateField from '../ui/DateField';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid2 as Grid,
  MenuItem,
  Typography,
  Box,
  Alert,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

// Schema de validação
const scheduleBlockSchema = z.object({
  professionalId: z.string().uuid('Selecione um profissional'),
  selectedPlaceIds: z.array(z.string()).min(1, 'Selecione pelo menos um local'),
  startDate: z.string().min(1, 'Data de início obrigatória'),
  endDate: z.string().min(1, 'Data de fim obrigatória'),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  isAllDay: z.boolean(),
  reason: z.string().max(500, 'Motivo deve ter no máximo 500 caracteres').optional(),
}).refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return new Date(data.endDate) >= new Date(data.startDate);
    }
    return true;
  },
  {
    message: 'Data de fim deve ser posterior ou igual à data de início',
    path: ['endDate'],
  }
);

/**
 * Formulário de Schedule Block (Ausência) com validação Zod
 */
export default function ScheduleBlockForm({
  open,
  onClose,
  onSubmit,
  initialData,
  isEdit = false,
  professionals = [],
  places = [],
  placeProfessionals = [],
  loading = false,
}) {
  const [professionalPlaces, setProfessionalPlaces] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(scheduleBlockSchema),
    mode: 'onChange',
    defaultValues: {
      professionalId: '',
      selectedPlaceIds: [],
      startDate: '',
      endDate: '',
      startTime: '08:00',
      endTime: '18:00',
      isAllDay: true,
      reason: '',
    },
  });

  const professionalId = watch('professionalId');
  const selectedPlaceIds = watch('selectedPlaceIds');
  const isAllDay = watch('isAllDay');

  // Atualizar lista de places do professional selecionado
  useEffect(() => {
    if (professionalId) {
      const places = placeProfessionals.filter(pp => pp.professionalId === professionalId);
      setProfessionalPlaces(places);
    } else {
      setProfessionalPlaces([]);
      setValue('selectedPlaceIds', []);
    }
  }, [professionalId, placeProfessionals, setValue]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        // Modo edição - extrair professional do placeProfessionalId
        const pp = placeProfessionals.find((p) => p.id === initialData.placeProfessionalId);
        if (pp) {
          // Converter UTC (backend) para BRT (formulário)
          const startLocal = initialData.startDate ? convertUTCtoBRT(initialData.startDate) : { date: '', time: '08:00' };
          const endLocal = initialData.endDate ? convertUTCtoBRT(initialData.endDate) : { date: '', time: '18:00' };
          
          // All-day se começar à meia-noite e terminar antes da 01:00
          const isAllDay = startLocal.time === '00:00' && (endLocal.time === '23:59' || endLocal.time === '00:00');
          
          reset({
            professionalId: pp.professionalId,
            selectedPlaceIds: [pp.placeId],
            startDate: startLocal.date,
            endDate: endLocal.date,
            startTime: isAllDay ? '08:00' : startLocal.time,
            endTime: isAllDay ? '18:00' : endLocal.time,
            isAllDay: isAllDay,
            reason: initialData.reason || '',
          });
        }
      } else {
        // Modo criação
        reset({
          professionalId: '',
          selectedPlaceIds: [],
          startDate: '',
          endDate: '',
          startTime: '08:00',
          endTime: '18:00',
          isAllDay: true,
          reason: '',
        });
      }
    }
  }, [initialData, open, reset, placeProfessionals]);

  const onSubmitForm = (formData) => {
    // Converter selectedPlaceIds para placeProfessionalIds
    const placeProfessionalIds = formData.selectedPlaceIds.map(placeId => {
      const pp = placeProfessionals.find(p => 
        p.professionalId === formData.professionalId && p.placeId === placeId
      );
      return pp?.id;
    }).filter(Boolean);

    // Montar timestamps convertendo de BRT para UTC
    // CRÍTICO: Usuário insere horário local (BRT), mas backend espera UTC
    const startDateTime = formData.isAllDay
      ? convertBRTtoUTC(formData.startDate, '00:00')
      : convertBRTtoUTC(formData.startDate, formData.startTime);
    
    const endDateTime = formData.isAllDay
      ? convertBRTtoUTC(formData.endDate, '23:59')
      : convertBRTtoUTC(formData.endDate, formData.endTime);

    onSubmit({
      placeProfessionalIds,
      startDate: startDateTime,
      endDate: endDateTime,
      reason: formData.reason,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePlaceCheckbox = (placeId) => {
    const currentSelection = selectedPlaceIds || [];
    const newSelection = currentSelection.includes(placeId)
      ? currentSelection.filter(id => id !== placeId)
      : [...currentSelection, placeId];
    
    setValue('selectedPlaceIds', newSelection, { shouldValidate: true });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Ausência' : 'Nova Ausência'}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Alert severity="info">
              <Typography variant="body2">
                Selecione o profissional e os locais onde ele não estará disponível.
              </Typography>
            </Alert>

            {/* Seleção de Profissional */}
            <TextField
              select
              label="Profissional"
              fullWidth
              {...register('professionalId')}
              error={!!errors.professionalId}
              helperText={errors.professionalId?.message}
              disabled={loading || isEdit}
              required
            >
              <MenuItem value="">
                <em>Selecione um profissional</em>
              </MenuItem>
              {professionals.map((prof) => (
                <MenuItem key={prof.id} value={prof.id}>
                  {prof.name}
                  {prof.specialty && ` - ${prof.specialty}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Checkboxes de Locais */}
            {professionalId && (
              <FormControl component="fieldset" variant="standard" error={!!errors.selectedPlaceIds}>
                <FormLabel component="legend">
                  <Typography variant="subtitle2" color="primary" fontWeight="bold">
                    Locais
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Selecione um ou mais locais para a ausência
                  </Typography>
                </FormLabel>
                <FormGroup>
                  {professionalPlaces.length === 0 ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Este profissional não tem vínculos com locais cadastrados.
                    </Alert>
                  ) : (
                    professionalPlaces.map((pp) => {
                      const place = places.find(p => p.id === pp.placeId);
                      return (
                        <FormControlLabel
                          key={pp.id}
                          control={
                            <Checkbox
                              checked={selectedPlaceIds?.includes(pp.placeId) || false}
                              onChange={() => handlePlaceCheckbox(pp.placeId)}
                              disabled={loading || isEdit}
                            />
                          }
                          label={place?.name || 'Local desconhecido'}
                        />
                      );
                    })
                  )}
                </FormGroup>
                {errors.selectedPlaceIds && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.selectedPlaceIds.message}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Período */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
                Período de Ausência
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Início"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.startDate}
                        helperText={errors.startDate?.message}
                        disabled={loading}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Fim"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.endDate}
                        helperText={errors.endDate?.message}
                        disabled={loading}
                        required
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Dia inteiro ou horário */}
            <FormControlLabel
              control={
                <Controller
                  name="isAllDay"
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value} disabled={loading} />
                  )}
                />
              }
              label="Dia inteiro (desmarque para especificar horário)"
            />

            {!isAllDay && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hora Início"
                    type="time"
                    fullWidth
                    {...register('startTime')}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hora Fim"
                    type="time"
                    fullWidth
                    {...register('endTime')}
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Motivo */}
            <TextField
              label="Motivo"
              fullWidth
              multiline
              rows={3}
              {...register('reason')}
              error={!!errors.reason}
              helperText={errors.reason?.message || 'Opcional - Ex: Férias, compromisso pessoal, etc'}
              disabled={loading}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid}>
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
